import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Autocomplete,
    TextField,
    Divider,
} from "@mui/material";
import { RouteSharp } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import AppForm from "components/Shared/AppForm";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";
import { useLazyGetDDLWarehouseListQuery } from "store/services/warehouseApi";
import { useLazyGetTransitShipmentsWithoutRouteQuery } from "store/services/warehousingApi";
import { useAssignBulkRoutesWarehouseMutation } from "store/services/shipmentApi";

const TransitBulkRoutesModalForm = ({ open, onClose, accountId, entityId }) => {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [fetchWarehouses, { data: warehousesData, isLoading: loadingWarehouses }] =
        useLazyGetDDLWarehouseListQuery();

    const [fetchTransitShipments, { data: shipmentsResponse, isFetching: loadingShipments }] =
        useLazyGetTransitShipmentsWithoutRouteQuery();

    const [fetchRoutes, { data: routesResponse, isLoading: loadingRoutes }] =
        useLazyGetDDLRouteShipmentsQuery();

    const [assignBulkRoutes, { isLoading: isSubmitting }] =
        useAssignBulkRoutesWarehouseMutation();

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            warehouse: null,
            shipments: [],
            routes: null
        },
    });

    const selectedWarehouse = watch("warehouse");
    const selectedShipments = watch("shipments");
    const selectedRoutes = watch("routes");

    const warehouses = warehousesData || [];
    const shipments = shipmentsResponse?.data || [];
    const routes = routesResponse?.data || routesResponse || [];

    const flattenedShipments = shipments.flatMap((warehousingRecord) =>
        warehousingRecord.shipmentId.map((shipment) => ({
            ...shipment,
            warehousingId: warehousingRecord.warehousingId,
            warehousingRecordId: warehousingRecord._id,
            warehouseId: warehousingRecord.warehouseId,
        }))
    );

    useEffect(() => {
        if (open && accountId && entityId) {
            fetchWarehouses({ account: accountId, entity: entityId });
            fetchRoutes({ account: accountId, entity: entityId });
            reset({ warehouse: null, shipments: [], routes: null });
            setError("");
            setSuccess("");
        }
    }, [open, accountId, entityId, fetchWarehouses, fetchRoutes, reset]);

    useEffect(() => {
        if (selectedWarehouse && accountId && entityId) {
            console.log("Fetching transit shipments for warehouse:", selectedWarehouse._id);
            fetchTransitShipments({
                account: accountId,
                entity: entityId,
                warehouseId: selectedWarehouse._id,
            });
            setValue("shipments", []);
        }
    }, [selectedWarehouse, accountId, entityId, fetchTransitShipments, setValue]);

    const onSubmit = async (data) => {
        if (!data.warehouse) {
            setError("Please select a warehouse.");
            return;
        }
        if (!data.shipments.length || !data.routes) {
            setError("Please select at least one shipment and a route.");
            return;
        }

        try {
            const payload = {
                shipmentIds: data.shipments.map((s) => s._id),
                routeIds: [data.routes._id],
                account: accountId,
                entity: entityId,
            };
            await assignBulkRoutes(payload).unwrap();
            setSuccess(`Routes assigned to ${data.shipments.length} shipment(s)!`);
            setTimeout(onClose, 1500);
        } catch (err) {
            setError(err?.data?.message || "Failed to assign routes.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    background: (theme) =>
                        theme.palette.mode === "dark"
                            ? "linear-gradient(90deg, rgb(167, 212, 248), rgb(146, 195, 241))"
                            : "linear-gradient(90deg, #1976d2, #42a5f5)",
                    color: "white",
                    fontWeight: 600,
                    py: 2,
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <RouteSharp />
                    <span>Bulk Assign Routes to Transit Shipments</span>
                </Box>
            </DialogTitle>
            <Divider />

            <AppForm loading={isSubmitting}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent sx={{ p: 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}
                        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Controller
                                name="warehouse"
                                control={control}
                                rules={{ required: "Please select a warehouse" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        options={warehouses}
                                        loading={loadingWarehouses}
                                        value={field.value ?? null}
                                        onChange={(_, value) => field.onChange(value)}
                                        getOptionLabel={(opt) =>
                                            `${opt.name} - ${opt.warehouse_id}`
                                        }
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Warehouse"
                                                placeholder="Select a warehouse..."
                                                variant="outlined"
                                                required
                                                error={!!errors.warehouse}
                                                helperText={
                                                    errors.warehouse?.message ||
                                                    "Select warehouse to load transit shipments"
                                                }
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="shipments"
                                control={control}
                                rules={{ required: "Select at least one shipment" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={flattenedShipments}
                                        loading={loadingShipments}
                                        value={field.value ?? []}
                                        onChange={(_, value) => field.onChange(value)}
                                        getOptionLabel={(opt) =>
                                            opt.shipmentId
                                                ? `${opt.shipmentId} - ${opt.warehousingId}`
                                                : "Unknown Shipment"
                                        }
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        disabled={!selectedWarehouse}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Transit Shipments"
                                                placeholder={
                                                    selectedWarehouse
                                                        ? "Select shipments..."
                                                        : "Select a warehouse first"
                                                }
                                                variant="outlined"
                                                error={!!errors.shipments}
                                                helperText={
                                                    errors.shipments?.message ||
                                                    `${field.value?.length ?? 0} shipment(s) selected`
                                                }
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="routes"
                                control={control}
                                rules={{ required: "Select a route" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        options={routes}
                                        loading={loadingRoutes}
                                        value={field.value ?? null}
                                        getOptionLabel={(opt) => opt?.routeName || ""}
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        onChange={(_, value) => field.onChange(value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Route"
                                                placeholder="Select a route..."
                                                variant="outlined"
                                                error={!!errors.routes}
                                                helperText={
                                                    errors.routes?.message ||
                                                    (field.value ? "1 route selected" : "0 routes selected")
                                                }
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={onClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<RouteSharp />}
                            disabled={
                                isSubmitting ||
                                !selectedWarehouse ||
                                !selectedShipments?.length ||
                                !selectedRoutes
                            }
                        >
                            {isSubmitting ? "Assigning..." : "Assign Routes"}
                        </Button>
                    </DialogActions>
                </form>
            </AppForm>
        </Dialog>
    );
};

export default TransitBulkRoutesModalForm;